import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { ProductStock, StockLocation } from "../shared/types";

const stockLocationsContext = createContext<{
    stockLocations: StockLocation[];
    errors: string[];
    loading: boolean;
    fetchStockLocations: () => void;
}>({
    stockLocations: [],
    errors: [],
    loading: false,
    fetchStockLocations: () => { },
});

function parseStockLocation(d: any): StockLocation {
    return {
        id: d.id,
        warehouse: d.warehouse,
        name: d.name,
        levels: d.levels,
        uuid: d.uuid,
        major: d.major,
        minor: d.minor,
    };
}

function useProviderStockLocations() {
    const [stockLocations, setStockLocations] = useState<StockLocation[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    useEffect(() => {
        if (stockLocations.length === 0) {
            fetchStockLocations();
        }
    }, []);

    async function fetchStockLocations() {
        setLoading(true);
        setErrors([]);

        try {
            let response = await apiService.get('/stock-locations');
            if (!response.ok) {
                setErrors([
                    'Loading inbound deliveries failed. Please try again.',
                ]);
                return;
            }
            let data: StockLocation[] = (await response.json()).map(
                parseStockLocation
            );

            setStockLocations(data);
        } catch (error) {
            console.error(error);
            setErrors([
                'Loading stock locations failed. Please try again.',
            ]);
        } finally {
            setLoading(false);
        }
    }

    return {
        stockLocations,
        errors,
        loading,
        fetchStockLocations,
    }
}

export function ProviderStockLocations({ children }: { children: React.ReactNode }) {
    const stockLocations = useProviderStockLocations();
    return <stockLocationsContext.Provider value={stockLocations}> {children}</stockLocationsContext.Provider>;
}

export const useStockLocations = () => {
    return useContext(stockLocationsContext);
}

function parseStock(d: any): ProductStock {
    return {
        ...d,
        expires_at: d.expires_at ? new Date(d.expires_at) : undefined,
    };
}

export type SortOrder = "asc" | "desc";

export function useStockLocationDetail(id: string | undefined) {
    const [stockLocation, setStockLocation] = useState<StockLocation | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    const [stockItems, setStockItems] = useState<ProductStock[]>([]);
    const [stockErrors, setStockErrors] = useState<string[]>([]);
    const [stockLoading, setStockLoading] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const [sortField, setSortField] = useState("expires_at");
    const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    function setSort(field: string) {
        if (field === sortField) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
        setPageInternal(1);
    }

    useEffect(() => {
        if (id) fetchStockLocation();
    }, [id]);

    useEffect(() => {
        if (id) fetchStockItems();
    }, [id, page, perPage, searchTerm, sortField, sortOrder]);

    async function fetchStockLocation() {
        if (!id) return;
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/stock-locations/${id}`);
            if (!response.ok) {
                setErrors(['Loading stock location failed. Please try again.']);
                return;
            }
            const json = await response.json();
            setStockLocation(parseStockLocation(json.data ?? json));
        } catch (error) {
            console.error(error);
            setErrors(['Loading stock location failed. Please try again.']);
        } finally {
            setLoading(false);
        }
    }

    async function fetchStockItems() {
        if (!id) return;
        setStockLoading(true);
        setStockErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(page),
                per_page: String(perPage),
                sort: sortField,
                order: sortOrder,
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            const response = await apiService.get(`/stock-locations/${id}/product-stock?${params.toString()}`);
            if (!response.ok) {
                setStockErrors(['Loading stock failed. Please try again.']);
                return;
            }
            const json = await response.json();
            const items: ProductStock[] = (json.data ?? json).map(parseStock);
            setTotal(json.meta?.total ?? items.length);
            setStockItems(items);
        } catch (error) {
            console.error(error);
            setStockErrors(['Loading stock failed. Please try again.']);
        } finally {
            setStockLoading(false);
        }
    }

    return {
        stockLocation, errors, loading, fetchStockLocation,
        stockItems, stockErrors, stockLoading, fetchStockItems,
        page, perPage, total, setPage, setPerPage,
        searchTerm, setSearchTerm, sortField, sortOrder, setSort,
    };
}